import { useEffect, useRef, useState, useCallback } from 'react'

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>
  threshold?: number // 引っ張る距離のしきい値（px）
  enabled?: boolean
}

interface UsePullToRefreshReturn {
  isPulling: boolean
  isRefreshing: boolean
  pullDistance: number
  pullProgress: number // 0-1の進捗
}

// モバイルデバイス（タッチ対応）かどうかを判定
const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

export function usePullToRefresh({
  onRefresh,
  threshold = 80,
  enabled = true
}: UsePullToRefreshOptions): UsePullToRefreshReturn {
  const [isPulling, setIsPulling] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)

  const startY = useRef(0)
  const currentY = useRef(0)
  const isAtTop = useRef(true)

  // タッチデバイス（モバイル）で有効
  const isEnabled = enabled && isTouchDevice()

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!isEnabled || isRefreshing) return

    // ページが最上部にあるかチェック
    isAtTop.current = window.scrollY <= 0
    if (!isAtTop.current) return

    startY.current = e.touches[0].clientY
    currentY.current = startY.current
  }, [isEnabled, isRefreshing])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isEnabled || isRefreshing || !isAtTop.current) return

    currentY.current = e.touches[0].clientY
    const diff = currentY.current - startY.current

    // 下方向にスワイプしている場合のみ
    if (diff > 0 && window.scrollY <= 0) {
      // 抵抗を加える（指の動きの半分だけ動く）
      const resistance = 0.5
      const distance = Math.min(diff * resistance, threshold * 1.5)

      setPullDistance(distance)
      setIsPulling(true)

      // デフォルトのスクロールを防ぐ（pull-to-refresh中のみ）
      if (distance > 10) {
        e.preventDefault()
      }
    }
  }, [isEnabled, isRefreshing, threshold])

  const handleTouchEnd = useCallback(async () => {
    if (!isEnabled || isRefreshing) return

    const distance = pullDistance

    setIsPulling(false)

    if (distance >= threshold) {
      // しきい値を超えたらリフレッシュ実行
      setIsRefreshing(true)
      setPullDistance(threshold) // リフレッシュ中は固定位置

      try {
        await onRefresh()
      } catch (error) {
        console.error('Pull-to-refresh error:', error)
      } finally {
        setIsRefreshing(false)
        setPullDistance(0)
      }
    } else {
      // しきい値未満なら元に戻す
      setPullDistance(0)
    }

    startY.current = 0
    currentY.current = 0
  }, [isEnabled, isRefreshing, pullDistance, threshold, onRefresh])

  useEffect(() => {
    if (!isEnabled) return

    const options: AddEventListenerOptions = { passive: false }

    document.addEventListener('touchstart', handleTouchStart, options)
    document.addEventListener('touchmove', handleTouchMove, options)
    document.addEventListener('touchend', handleTouchEnd)
    document.addEventListener('touchcancel', handleTouchEnd)

    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
      document.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [isEnabled, handleTouchStart, handleTouchMove, handleTouchEnd])

  return {
    isPulling,
    isRefreshing,
    pullDistance,
    pullProgress: Math.min(pullDistance / threshold, 1)
  }
}
